import React from 'react'
import { motion } from 'motion/react'
import {
  ArrowRightIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  ClipboardDocumentCheckIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
} from '@heroicons/react/20/solid'

const operatingAreas = [
  {
    title: 'Member records',
    body: 'Keep lodge rosters, roles, profiles, and member cards in one place.',
    icon: UserGroupIcon,
  },
  {
    title: 'Voucher approvals',
    body: 'Move reimbursement requests from receipt upload to board review to payment.',
    icon: ClipboardDocumentCheckIcon,
  },
  {
    title: 'Events and committees',
    body: 'Connect lodge calendars, committee hosts, attendance interest, and event financials.',
    icon: CalendarDaysIcon,
  },
  {
    title: 'Payments and accounts',
    body: 'Track cash, checks, funding accounts, and event income without a separate spreadsheet.',
    icon: BanknotesIcon,
  },
]

const workflowSteps = [
  'A member submits a voucher, payment, event, or rental request.',
  'The right officers see what needs review in the lodge dashboard.',
  'Board and treasurer actions are recorded against the member and lodge.',
  'Members get a cleaner experience without chasing paper forms.',
]

/**
 * The two dashboard captures are real screenshots of the product, so they carry
 * the page's visual weight. They used to sit behind the hero copy at low
 * opacity under a gradient mask, which wasted the only real imagery the site
 * has.
 */
function DashboardShot({ className = '' }: { className?: string }) {
  return (
    <div
      className={`overflow-hidden rounded-xl border border-[color:var(--theme-border)] bg-[color:var(--theme-card)] shadow-e4 ${className}`}
    >
      <img
        src="/dashboard_example.png"
        alt="The LodgeLink dashboard listing a lodge's members, vouchers, and events"
        width={1000}
        height={1000}
        className="hidden w-full dark:block"
      />
      <img
        src="/dashboard_example_light_mode.png"
        alt="The LodgeLink dashboard listing a lodge's members, vouchers, and events"
        width={1000}
        height={1000}
        className="block w-full dark:hidden"
      />
    </div>
  )
}

export function HeroSection() {
  const rise = (delay = 0) => ({
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.25 },
    transition: { duration: 0.38, delay, ease: [0.22, 1, 0.36, 1] as const },
  })

  return (
    <main className="theme-bg overflow-hidden">
      {/* 1. Hero: asymmetric split, copy left, the real product shot right. */}
      <section className="border-b border-[color:var(--theme-border-soft)] px-4 pt-16 pb-20 sm:px-6 lg:px-8 lg:pt-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="mb-6 inline-flex items-center gap-2 border-l-2 border-[color:var(--theme-brand)] pl-3 text-sm font-medium text-[color:var(--theme-muted)]">
              Operations software for Elks lodges
            </p>
            {/* Kept to ~27 characters so it sets on two lines in this
                            column at the display size. The specifics live in the
                            subtext below rather than in a third headline line. */}
            <h1 className="display-1 text-[color:var(--theme-fg)]">The whole lodge, one place.</h1>
            <p className="measure-wide mt-6 text-lg leading-8 text-[color:var(--theme-muted)]">
              LodgeLink handles membership, vouchers, events, hall rentals, and lodge finances, so
              officers stop chasing paper forms.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <a
                href="/find-a-lodge"
                className="theme-button-brand inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold"
              >
                <MagnifyingGlassIcon className="size-5" />
                Find a lodge
              </a>
              <a
                href="/sign_in"
                className="group inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-[color:var(--theme-border)] bg-[color:var(--theme-surface)] px-5 py-3 text-sm font-semibold text-[color:var(--theme-fg)] transition-control hover:-translate-y-0.5 hover:bg-[color:var(--theme-hover)] hover:shadow-e2 active:translate-y-0 active:scale-[0.985]"
              >
                Member sign in
                <ArrowRightIcon className="size-4 transition-transform duration-fast ease-[var(--ease-out-soft)] group-hover:translate-x-0.5" />
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.38, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:-mr-24 xl:-mr-32"
          >
            <DashboardShot />
          </motion.div>
        </div>
      </section>

      {/* 2. Bordered 2x2 grid. No eyebrow: the headline names the section. */}
      <section className="border-b border-[color:var(--theme-border-soft)] px-4 pt-20 pb-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <motion.h2 {...rise()} className="display-2 measure-wide text-[color:var(--theme-fg)]">
            The daily lodge work is already connected.
          </motion.h2>

          <div className="mt-12 grid border-t border-[color:var(--theme-border-soft)] sm:grid-cols-2">
            {operatingAreas.map((area, index) => {
              const Icon = area.icon

              return (
                <motion.div
                  key={area.title}
                  {...rise(index * 0.06)}
                  className="group border-b border-[color:var(--theme-border-soft)] py-8 sm:odd:pr-10 sm:even:border-l sm:even:pl-10"
                >
                  <div className="flex items-start gap-4">
                    <span className="mt-1 flex size-9 shrink-0 items-center justify-center rounded-lg bg-[color:var(--theme-brand-soft)] text-[color:var(--theme-brand-text)] transition duration-fast ease-[var(--ease-out-soft)] group-hover:bg-[color:var(--theme-brand)] group-hover:text-[color:var(--theme-brand-contrast)]">
                      <Icon className="size-5" />
                    </span>
                    <div>
                      <h3 className="text-base font-semibold text-[color:var(--theme-fg)]">
                        {area.title}
                      </h3>
                      <p className="mt-2 text-sm leading-6 text-[color:var(--theme-muted)]">
                        {area.body}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* 3. Full-width headline over four ruled columns. */}
      <section className="border-b border-[color:var(--theme-border-soft)] px-4 pt-20 pb-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <motion.div {...rise()}>
            <p className="eyebrow">How it works</p>
            <h2 className="display-2 measure-wide mt-3 text-[color:var(--theme-fg)]">
              Clear next steps for every role.
            </h2>
          </motion.div>

          {/* gap-x matters here: without it the four top rules abut and
                        read as one bar across the section instead of as four marks. */}
          <ol className="mt-14 grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-x-10">
            {workflowSteps.map((step, index) => (
              <motion.li
                key={step}
                {...rise(index * 0.07)}
                className="border-t-2 border-[color:var(--theme-brand)] pt-5"
              >
                <span className="numeric font-mono text-xs font-medium tracking-[0.12em] text-[color:var(--theme-brand-text)]">
                  0{index + 1}
                </span>
                <p className="mt-3 text-base leading-7 text-[color:var(--theme-fg)]">{step}</p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* 4. Media-led section: the second real capture, at width. */}
      <section className="border-b border-[color:var(--theme-border-soft)] bg-[color:var(--theme-card)] px-4 pt-20 pb-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl text-center">
          <motion.h2 {...rise()} className="display-2 text-[color:var(--theme-fg)]">
            One dashboard for the whole lodge.
          </motion.h2>
          <motion.p
            {...rise(0.05)}
            className="measure mx-auto mt-4 text-base leading-7 text-[color:var(--theme-muted)]"
          >
            Members get simple forms. Officers get queues. Treasurers get records tied back to the
            lodge.
          </motion.p>
          <motion.div {...rise(0.1)} className="mt-12">
            <DashboardShot />
          </motion.div>
        </div>
      </section>

      {/* 5. Closing band. */}
      <section className="relative isolate overflow-hidden px-4 pt-20 pb-24 sm:px-6 lg:px-8">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_120%_at_20%_0%,var(--theme-brand-soft)_0%,transparent_60%)]"
        />
        <div className="relative mx-auto flex max-w-7xl flex-col gap-8 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <h2 className="display-2 text-[color:var(--theme-fg)]">
              Start with the lodge you already belong to.
            </h2>
            <p className="measure mt-4 text-base leading-7 text-[color:var(--theme-muted)]">
              Find a local lodge, or sign in to the account your lodge already uses.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <a
              href="/find-a-lodge"
              className="theme-button-brand inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold"
            >
              <MagnifyingGlassIcon className="size-5" />
              Find a lodge
            </a>
            <a
              href="/sign_in"
              className="inline-flex min-h-11 items-center justify-center rounded-lg border border-[color:var(--theme-border)] bg-[color:var(--theme-surface)] px-5 py-3 text-sm font-semibold text-[color:var(--theme-fg)] transition-control hover:bg-[color:var(--theme-hover)] active:scale-[0.985]"
            >
              Member sign in
            </a>
          </div>
        </div>
      </section>
    </main>
  )
}
